import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { LuQrCode } from 'react-icons/lu';

import Modal from '../../../../components/Modal/Modal';
import { getChargePix } from '../../../../services/finance/billingApi';
import {
   FieldLabel,
   PrimaryActionButton,
   SkeletonBlock,
} from '../../../../styles/dunning/base';
import {
   PixCodeInput,
   PixCopyRow,
   PixQrPlaceholder,
} from '../../../../styles/dunning/modals';
import { financeErrorMessage } from '../../utils/apiError';

// ---------------------------------------------------------------------------
// PIX do título. A busca é LAZY — só quando o modal abre (nunca no mount da
// tela: cada chamada consulta a ASAAS, que tem rate limit — handoff 0010). O
// código não fica em estado global; fecha o modal, descarta.
// ---------------------------------------------------------------------------

export default function PixModal({ isOpen, member = null, onClose }) {
   const [isLoading, setIsLoading] = useState(false);
   const [pix, setPix] = useState(null);

   useEffect(() => {
      if (!isOpen || !member) {
         setPix(null);
         return undefined;
      }

      let cancelled = false;
      setIsLoading(true);
      getChargePix(member.charge_id)
         .then((result) => {
            if (cancelled) return;
            if (!result.pixCopyPaste) {
               toast.error(
                  'Não foi possível obter o código PIX. Tente novamente em alguns instantes.'
               );
               onClose();
               return;
            }
            setPix(result);
         })
         .catch((err) => {
            if (cancelled) return;
            toast.error(
               financeErrorMessage(
                  err,
                  'Não foi possível obter o código PIX. Tente novamente em alguns instantes.'
               )
            );
            onClose();
         })
         .finally(() => {
            if (!cancelled) setIsLoading(false);
         });

      return () => {
         cancelled = true;
      };
      // `onClose` estável vem do container; member.charge_id identifica a busca.
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [isOpen, member?.charge_id]);

   const handleCopy = async () => {
      if (!pix?.pixCopyPaste) return;
      try {
         await navigator.clipboard.writeText(pix.pixCopyPaste);
         toast.success('Código PIX copiado.');
      } catch {
         toast.error('Não foi possível copiar. Selecione o código manualmente.');
      }
   };

   return (
      <Modal
         isOpen={isOpen}
         title="PIX do título"
         onClose={onClose}
         maxWidth="460px"
      >
         <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {isLoading ? (
               <>
                  <SkeletonBlock $h="180px" $radius="12px" />
                  <SkeletonBlock $h="40px" $radius="10px" />
               </>
            ) : (
               <>
                  <PixQrPlaceholder>
                     {pix?.pixQrCodeUrl ? (
                        <img src={pix.pixQrCodeUrl} alt="QR Code PIX" />
                     ) : (
                        <>
                           <LuQrCode size={34} />
                           <span>Use o código copia e cola abaixo</span>
                        </>
                     )}
                  </PixQrPlaceholder>
                  <div>
                     <FieldLabel htmlFor="dunning-pix-code">
                        PIX copia e cola
                     </FieldLabel>
                     <PixCopyRow>
                        <PixCodeInput
                           id="dunning-pix-code"
                           value={pix?.pixCopyPaste ?? ''}
                           readOnly
                        />
                        <PrimaryActionButton type="button" onClick={handleCopy}>
                           Copiar
                        </PrimaryActionButton>
                     </PixCopyRow>
                  </div>
               </>
            )}
         </div>
      </Modal>
   );
}

PixModal.propTypes = {
   isOpen: PropTypes.bool.isRequired,
   // eslint-disable-next-line react/forbid-prop-types
   member: PropTypes.object,
   onClose: PropTypes.func.isRequired,
};
